import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../api/axiosConfig";
import Swal from "sweetalert2";
import Select from "react-select";
import "../../index.css";
import MainLayout from "../MainLayout";

const ClientesSelect = ({ index, handleClienteChange, clientePreseleccionado }) => {
  const [clientes, setClientes] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);

  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const response = await axios.get("/clientes");
        const clientesData = response.data.data.map((cliente) => ({
          value: cliente.id,
          label: `${cliente.nombreCompleto} ${cliente.email || ""}`,
        }));
        setClientes(clientesData);
        
        // Si hay un cliente preseleccionado, encontrarlo y seleccionarlo
        if (clientePreseleccionado) {
          const clienteEncontrado = clientesData.find(
            (c) => c.value === clientePreseleccionado.id
          );
          if (clienteEncontrado) {
            setSelectedCliente(clienteEncontrado);
            // Notificar al componente padre sobre la selección
            handleClienteChange(index, clientePreseleccionado.id);
          }
        }
      } catch (error) {
        console.error("Error al obtener clientes", error);
      }
    };
    fetchClientes();
  }, []);

  // Efecto separado para manejar cambios en clientePreseleccionado
  useEffect(() => {
    if (clientePreseleccionado && clientes.length > 0) {
      const clienteEncontrado = clientes.find(
        (c) => c.value === clientePreseleccionado.id
      );
      if (clienteEncontrado && (!selectedCliente || selectedCliente.value !== clienteEncontrado.value)) {
        setSelectedCliente(clienteEncontrado);
        handleClienteChange(index, clientePreseleccionado.id);
      }
    }
  }, [clientePreseleccionado, clientes, index, handleClienteChange, selectedCliente]);

  return (
    <Select
      options={clientes}
      value={selectedCliente}
      onChange={(selectedOption) => {
        setSelectedCliente(selectedOption);
        handleClienteChange(index, selectedOption.value);
      }}
      placeholder="Selecciona un paciente"
    />
  );
};

const Pedidos = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const cliente = location.state?.cliente;
  const sucursales = location.state?.sucursales;
  const visitaData = location.state?.visitaData; // Datos de la visita si viene desde CrearVisita

  // Verificar si el cliente está disponible, pero usar useEffect para evitar redirección inmediata
  useEffect(() => {
    // Solo redirigir si realmente no hay estado de navegación y no hay cliente
    // Esto permite que el estado de navegación se procese correctamente
    if (!location.state && !cliente) {
      // Esperar un momento para asegurar que el estado de navegación se haya procesado
      const timer = setTimeout(() => {
        if (!location.state && !cliente) {
          navigate("/dashboard/clientes");
        }
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [cliente, location.state, navigate]);
  const handleClienteChange = (index, clienteId) => {
    const nuevosElementos = [...elementos];
    nuevosElementos[index].idCliente = clienteId;
    setElementos(nuevosElementos);
  };
  const [fechaEntrega, setFechaEntrega] = useState("");
  const [elementos, setElementos] = useState([
    {
      armazon: "",
      lente: "",
      otro: "",
      importeArmazon: "",
      importeLente: "",
      importeOtro: "",
      grado: "",
      tono: "",
      observaciones: "",
      idCliente: null,
    },
  ]);
  const [tipoPago, setTipoPago] = useState("total");
  const [anticipo, setAnticipo] = useState(0);
  const [observaciones, setObservaciones] = useState(visitaData?.observaciones || "");
  const [formaPago, setFormaPago] = useState("");
  const [facturacion, setFacturacion] = useState("False");
  const [tipoTicketFis, setTipoTicketFis] = useState("Comprobante");

  // Auto-rellenar elementos con datos de la visita si están disponibles
  useEffect(() => {
    if (visitaData && cliente) {
      // Auto-rellenar observaciones si vienen de la visita
      if (visitaData.observaciones) {
        setObservaciones(visitaData.observaciones);
      }
      
      // Si hay datos de graduación, sugerir en el primer elemento
      if (elementos.length > 0 && (visitaData.esfOd || visitaData.esfOi || visitaData.cilOd || visitaData.cilOi)) {
        const nuevoElemento = {
          ...elementos[0],
          idCliente: cliente.id, // Autoseleccionar el cliente en el primer elemento
          observaciones: visitaData.observaciones || elementos[0].observaciones || "",
        };
        
        // Si hay información de lente de contacto, agregarla a observaciones
        if (visitaData.lenteContactoDes) {
          nuevoElemento.observaciones = (nuevoElemento.observaciones ? nuevoElemento.observaciones + "\n" : "") + 
            `Lente de contacto: ${visitaData.lenteContactoDes}`;
        }
        
        // Si hay información de DI/AO, agregarla
        if (visitaData.diData || visitaData.aoData) {
          const diAoInfo = [];
          if (visitaData.diData) diAoInfo.push(`DI: ${visitaData.diData}`);
          if (visitaData.aoData) diAoInfo.push(`AO: ${visitaData.aoData}`);
          nuevoElemento.observaciones = (nuevoElemento.observaciones ? nuevoElemento.observaciones + "\n" : "") + 
            diAoInfo.join(", ");
        }
        
        // Agregar información de graduación a observaciones
        const graduacionInfo = [];
        if (visitaData.esfOd || visitaData.esfOi) {
          graduacionInfo.push(`ESF OD: ${visitaData.esfOd || "N/A"}, OI: ${visitaData.esfOi || "N/A"}`);
        }
        if (visitaData.cilOd || visitaData.cilOi) {
          graduacionInfo.push(`CIL OD: ${visitaData.cilOd || "N/A"}, OI: ${visitaData.cilOi || "N/A"}`);
        }
        if (visitaData.ejeOd || visitaData.ejeOi) {
          graduacionInfo.push(`EJE OD: ${visitaData.ejeOd || "N/A"}, OI: ${visitaData.ejeOi || "N/A"}`);
        }
        if (visitaData.addOd || visitaData.addOi) {
          graduacionInfo.push(`ADD OD: ${visitaData.addOd || "N/A"}, OI: ${visitaData.addOi || "N/A"}`);
        }
        
        if (graduacionInfo.length > 0) {
          nuevoElemento.observaciones = (nuevoElemento.observaciones ? nuevoElemento.observaciones + "\n" : "") + 
            "Graduación: " + graduacionInfo.join(" | ");
        }
        
        setElementos([nuevoElemento]);
      } else if (elementos.length > 0 && cliente) {
        // Si no hay datos de graduación pero hay cliente, al menos establecer el cliente
        const nuevoElemento = {
          ...elementos[0],
          idCliente: cliente.id,
        };
        setElementos([nuevoElemento]);
      }
    }
  }, [visitaData, cliente]);

  // Función para calcular el total
  const calcularTotal = () => {
    return elementos.reduce((total, elemento) => {
      const importeArmazon = parseFloat(elemento.importeArmazon) || 0;
      const importeLente = parseFloat(elemento.importeLente) || 0;
      const importeOtro = parseFloat(elemento.importeOtro) || 0;
      total += importeArmazon + importeLente + importeOtro;
      return total;
    }, 0);
  };
  const saldo =
    tipoPago === "Anticipo"
      ? calcularTotal() -
        (isNaN(anticipo) ? (setAnticipo(null), null) : anticipo)
      : null;
  // Función para manejar el cambio en el tipo de pago
  const handleTipoPagoChange = (e) => {
    setTipoPago(e.target.value);
    setAnticipo();
  };

  // Función para manejar el cambio en la anticipo
  const handleAnticipoChange = (e) => {
    setAnticipo(parseFloat(e.target.value));
  };

  // Función para manejar el cambio en la forma de pago
  const handleFormaPagoChange = (e) => {
    setFormaPago(e.target.value);
  };

  // Función para manejar el cambio en facturación
  const handleFacturacionChange = (e) => {
    setFacturacion(e.target.value);
  };

  // Función para manejar el cambio en ticket tipo fiscal
  const handleTicketTipoChange = (e) => {
    setTipoTicketFis(e.target.value);
  };

  // Función para manejar el envío del formulario
  const handleSubmit = (e) => {
    e.preventDefault();

    const sucursalID = localStorage.getItem("branchId");
    const sucursalActual = sucursales.find((suc) => suc.id == sucursalID);

    // Obtener todos los datos del formulario
    const formData = {
      idCliente: cliente.id,
      nombreCliente: cliente.nombreCompleto,
      idSucursal: sucursalID,
      direccionSucursal: sucursalActual.direccion || "",
      idCreador: localStorage.getItem("idUser"),
      nombreCreador: `${localStorage.getItem("name")} ${localStorage.getItem(
        "lastName"
      )} ${localStorage.getItem("secondLastName")}`,
      ultimoModificador: `${localStorage.getItem(
        "name"
      )} ${localStorage.getItem("lastName")} ${localStorage.getItem(
        "secondLastName"
      )}`,
      idUltimoModificador: localStorage.getItem("idUser"),
      estatus: "En proceso de elaboración",
      fechaEntrega: fechaEntrega,
      tipoPago: tipoPago || "Total",
      observaciones: observaciones,
      formaPago: formaPago || "Efectivo",
      facturacion: facturacion,
      total: calcularTotal(),
      saldoPendiente:
        tipoPago === "Anticipo" ? calcularTotal() - (anticipo || 0) : 0,
      importe: tipoPago === "Anticipo" ? anticipo || 0 : calcularTotal(),
      tipoTicketFis,
      anticipo,
      elementos,
      idVisita: visitaData?.idVisita || null, // Incluir idVisita si viene desde el flujo de visita
    };

    axios
      .post("/crearPedido", formData)
      .then((response) => {
        if (response.data.success) {
          axios
            .get(`/clienteTel/${cliente.id}`)
            .then((response) => {
              const fechaSecciones = fechaEntrega.split("-");
              const anio = fechaSecciones[0];
              const mes = fechaSecciones[1];
              const dia = fechaSecciones[2];
              const fechaEntregaFormato = `${dia}/${mes}/${anio}`;
              const telefono = response.data.data[0].telefono;
              if (telefono != "" && telefono != "0000000000") {
                axios
                  .post("/whatsapp/AvisoCreacionPedidoFechaEntrega", {
                    fechaEntrega: fechaEntregaFormato,
                    nombreCliente: cliente.nombreCompleto,
                    phoneNumber: "+52" + telefono,
                  })
                  .then((response) => {
                    console.log(response);
                  });
              }
            })
            .catch((error) => {
              console.error("Error al obtener el teléfono del cliente:", error);
            });

          Swal.fire({
            title: "¡Pedido creado!",
            text: "El nuevo pedido ha sido creado exitosamente.",
            icon: "success",
            confirmButtonText: "Ir a tickets",
            allowOutsideClick: "false",
          }).then((result) => {
            if (result.isConfirmed) {
              const idPedido = response.data.pedidoId;
              navigate("/pedido/tickets", { state: { idPedido } });
            }
          });
        } else {
          Swal.fire({
            title: "¡Error!",
            text: "No se pudo crear el pedido.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      })
      .catch((error) => {
        console.error("Error al crear pedido:", error);
        Swal.fire({
          title: "¡Error!",
          text: "Ocurrió un error al intentar crear el pedido.",
          icon: "error",
          confirmButtonText: "OK",
        });
      });
  };
  // Función para agregar un nuevo elemento
  const handleAddElemento = () => {
    setElementos([
      ...elementos,
      {
        armazon: "",
        lente: "",
        otro: "",
        importeArmazon: "",
        importeLente: "",
        importeOtro: "",
        grado: "",
        tono: "",
        observaciones: "",
      },
    ]);
  };
  // Función para eliminar un elemento
  const handleRemoveElemento = (index) => {
    if (elementos.length > 1) {
      setElementos(elementos.filter((_, i) => i !== index));
    }
  };

  // Función para manejar el cambio en un elemento específico
  const handleElementoChange = (index, e) => {
    const { name, value } = e.target;
    const updatedElementos = elementos.map((elemento, i) =>
      i === index ? { ...elemento, [name]: value } : elemento
    );
    setElementos(updatedElementos);
  };

  return (
    <MainLayout>
      <form
        onSubmit={handleSubmit}
        className="container formCliente"
      >
        <h2 className="mb-3">
          <b>Nuevo Pedido:</b> {cliente.nombreCompleto}
        </h2>
        <div className="row mb-3">
          <div className="col-md-4">
            <label htmlFor="fechaEntrega">Fecha de Entrega Prevista:</label>
            <input
              type="date"
              className="form-control"
              id="fechaEntrega"
              value={fechaEntrega}
              onChange={(e) => setFechaEntrega(e.target.value)}
              required
            />
          </div>

          <div className="col-md-4">
            <label htmlFor="formaPago">Tipo Ticket:</label>
            <select
              className="form-control"
              id="tipoTicket"
              value={tipoTicketFis}
              onChange={handleTicketTipoChange}
              required
            >
              <option value="Comprobante">Comprobante</option>
              <option value="Nota Simple">Nota Simple</option>
            </select>
          </div>
        </div>
        {elementos.map((elemento, index) => (
          <div key={index}>
            <div className="row mb-3">
              <div className="col-md-4">
                <label htmlFor={`armazon-${index}`}>Armazón:</label>
                <input
                  type="text"
                  className="form-control"
                  id={`armazon-${index}`}
                  name="armazon"
                  value={elemento.armazon}
                  onChange={(e) => handleElementoChange(index, e)}
                  placeholder="Ingresa armazón"
                  required
                />
              </div>
              <div className="col-md-4">
                <label htmlFor={`lente-${index}`}>Lente:</label>
                <input
                  type="text"
                  className="form-control"
                  id={`lente-${index}`}
                  name="lente"
                  value={elemento.lente}
                  onChange={(e) => handleElementoChange(index, e)}
                  placeholder="Ingresa lente"
                />
              </div>
              <div className="col-md-4">
                <label htmlFor={`otro-${index}`}>Otro:</label>
                <input
                  type="text"
                  className="form-control"
                  id={`otro-${index}`}
                  name="otro"
                  value={elemento.otro}
                  onChange={(e) => handleElementoChange(index, e)}
                  placeholder="Ingresa si existe algun otro"
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-4">
                <label>Importe:</label>
                <input
                  type="number"
                  className="form-control"
                  name="importeArmazon"
                  placeholder="0"
                  value={elemento.importeArmazon}
                  onChange={(e) => handleElementoChange(index, e)}
                  required
                />
              </div>
              <div className="col-md-4">
                <label>Importe:</label>
                <input
                  type="number"
                  className="form-control"
                  name="importeLente"
                  placeholder="0"
                  value={elemento.importeLente}
                  onChange={(e) => handleElementoChange(index, e)}
                />
              </div>
              <div className="col-md-4">
                <label>Importe:</label>
                <input
                  type="number"
                  className="form-control"
                  name="importeOtro"
                  placeholder="0"
                  value={elemento.importeOtro}
                  onChange={(e) => handleElementoChange(index, e)}
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-4">
                <label htmlFor={`grado-${index}`}>Grado:</label>
                <input
                  type="text"
                  className="form-control"
                  id={`grado-${index}`}
                  name="grado"
                  value={elemento.grado}
                  onChange={(e) => handleElementoChange(index, e)}
                  placeholder="Ingresa grado"
                />
              </div>
              <div className="col-md-4">
                <label>Tono:</label>
                <input
                  type="text"
                  className="form-control"
                  name="tono"
                  value={elemento.tono}
                  onChange={(e) => handleElementoChange(index, e)}
                  placeholder="Ingresa tono"
                />
              </div>
              <div className="col-md-4">
                <label>Observaciones:</label>
                <textarea
                  type="text"
                  className="form-control"
                  name="observaciones"
                  value={elemento.observaciones}
                  onChange={(e) => handleElementoChange(index, e)}
                  placeholder="Observaciones adicionales"
                  style={{ width: "400px", height: "80px" }}
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-8">
                <ClientesSelect
                  index={index}
                  handleClienteChange={handleClienteChange}
                  clientePreseleccionado={index === 0 && cliente ? cliente : null}
                />
              </div>
              <div className="col-md-4"></div>
            </div>
            <button
              type="button"
              className="btn btn-danger mt-2"
              onClick={() => handleRemoveElemento(index)}
            >
              Eliminar Elemento
            </button>
          </div>
        ))}
        <div className="row mb-3">
          <div className="col-md-12">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddElemento}
            >
              Agregar Elemento
            </button>
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-md-4">
            <label htmlFor="tipoPago">Tipo de Pago:</label>
            <select
              className="form-control"
              id="tipoPago"
              value={tipoPago}
              onChange={handleTipoPagoChange}
              required
            >
              <option value="total">Total</option>
              <option value="Anticipo">Anticipo</option>
            </select>
          </div>
          {tipoPago === "Anticipo" && (
            <div className="col-md-4">
              <label htmlFor="anticipo">Anticipo:</label>
              <input
                type="number"
                className="form-control"
                id="anticipo"
                placeholder="0"
                value={anticipo}
                onChange={handleAnticipoChange}
                required
              />
            </div>
          )}
          <div className="col-md-4">
            <label htmlFor="formaPago">Forma de Pago:</label>
            <select
              className="form-control"
              id="formaPago"
              value={formaPago}
              onChange={handleFormaPagoChange}
              required
            >
              <option value="Efectivo">Efectivo</option>
              <option value="Transferencia">Transferencia</option>
              <option value="Credito">Credito</option>
              <option value="Debito">Debito</option>
            </select>
          </div>
          <div className="col-md-4">
            <label htmlFor="facturacion">Facturación:</label>
            <select
              className="form-control"
              id="facturacion"
              value={facturacion}
              onChange={handleFacturacionChange}
              required
            >
              <option value="True">Sí</option>
              <option value="False">No</option>
            </select>
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-md-4">
            <label htmlFor="observaciones">Observaciones:</label>
            <textarea
              className="form-control"
              id="observaciones"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              rows="3"
            />
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-md-4">
            <label>Total:</label>
            <input
              type="text"
              className="form-control"
              value={`$${calcularTotal().toFixed(2)}`}
              readOnly
            />
          </div>
          {tipoPago === "Anticipo" && (
            <div className="col-md-4">
              <label>Saldo Pendiente:</label>
              <input
                type="text"
                className="form-control"
                value={`$${saldo.toFixed(2)}`}
                readOnly
              />
            </div>
          )}
        </div>
        <div className="row mb-3">
          <div className="col-md-12">
            <button type="submit" className="btn btn-success">
              Guardar Pedido
            </button>
          </div>
        </div>
      </form>
    </MainLayout>
  );
};

export default Pedidos;
